/**
 * Date and time formatting utilities for Loops
 * Ensures strict timezone consistency (Europe/Zurich) across local and cloud environments.
 */

export const TIMEZONE = 'Europe/Zurich';

export function parseToDate(val) {
  if (!val) return null;
  if (val instanceof Date) return isNaN(val.getTime()) ? null : val;
  const str = String(val).trim();
  if (/^\d{1,2}:\d{2}(:\d{2})?$/.test(str)) return null;
  let d = new Date(str);
  if (!isNaN(d.getTime())) return d;
  if (/^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}/.test(str)) {
    d = new Date(str.replace(' ', 'T'));
    if (!isNaN(d.getTime())) return d;
  }
  return null;
}

export function formatLocalTimeHHMM(val, fallback = '--:--') {
  if (!val) return fallback;
  const str = String(val).trim();
  if (/^\d{1,2}:\d{2}(:\d{2})?$/.test(str)) {
    return str.slice(0, 5);
  }
  const d = parseToDate(val);
  if (d) {
    try {
      return new Intl.DateTimeFormat('fr-CH', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        timeZone: TIMEZONE
      }).format(d);
    } catch (e) {
      const h = String(d.getHours()).padStart(2, '0');
      const m = String(d.getMinutes()).padStart(2, '0');
      return `${h}:${m}`;
    }
  }
  return str.length >= 5 ? str.slice(0, 5) : (str || fallback);
}

export function getZurichOffset(dateStr) {
  const cleanDate = (dateStr || new Date().toISOString().slice(0, 10)).trim().slice(0, 10);
  try {
    const probe = new Date(`${cleanDate}T12:00:00Z`);
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: TIMEZONE,
      timeZoneName: 'longOffset',
      hour12: false
    }).formatToParts(probe);
    const tzPart = parts.find(p => p.type === 'timeZoneName');
    const match = tzPart ? tzPart.value.match(/GMT([+-]\d{2}):?(\d{2})/) : null;
    return match ? `${match[1]}:${match[2]}` : '+02:00';
  } catch (e) {
    return '+02:00';
  }
}

export function localTimeToISO(dateStr, timeHHMM) {
  if (!timeHHMM || !String(timeHHMM).trim()) return null;
  const tStr = String(timeHHMM).trim();
  if (tStr.includes('T')) {
    const d = new Date(tStr);
    return isNaN(d.getTime()) ? null : d.toISOString();
  }
  const cleanDate = (dateStr || new Date().toISOString().slice(0, 10)).trim().slice(0, 10);
  const timeMatch = tStr.match(/^(\d{1,2}):(\d{2})/);
  if (!timeMatch) return null;
  const padH = timeMatch[1].padStart(2, '0');
  const padM = timeMatch[2];
  const offset = getZurichOffset(cleanDate);
  return `${cleanDate}T${padH}:${padM}:00${offset}`;
}

export function calcDepartureTimeStr(arrivalTimeFormatted, serviceMins) {
  const [hStr, mStr] = (arrivalTimeFormatted || '08:00').split(':');
  const totalMins = (parseInt(hStr || 8, 10) * 60) + parseInt(mStr || 0, 10) + parseInt(serviceMins || 10, 10);
  const depH = Math.floor(totalMins / 60) % 24;
  const depM = totalMins % 60;
  return `${String(depH).padStart(2, '0')}:${String(depM).padStart(2, '0')}`;
}

export function formatDurationSec(totalSec = 0) {
  const h = Math.floor((totalSec || 0) / 3600);
  const m = Math.floor(((totalSec || 0) % 3600) / 60);
  if (h === 0) return `${m} min`;
  return `${h}h ${m}m`;
}
